import React, { useCallback, useEffect, useState } from "react";
import { api, formatCurrency, formatDateBR, formatCPF, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Download, Printer, Truck } from "lucide-react";

const emptyForm = {
    cliente_nome: "", cliente_documento: "", telefone: "",
    origem: "", destino: "",
    tipo_carga: "", peso_volume: "",
    responsavel_nome: "", cpf: "",
    veiculo_modelo: "", veiculo_placa: "",
    data: todayISO(), valor: "", observacoes: "",
};

export default function Freights() {
    const [freights, setFreights] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [filter, setFilter] = useState("");
    const [mes, setMes] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (mes) params.mes = mes;
        const [f, d, v] = await Promise.all([
            api.get("/freights", { params }),
            api.get("/drivers"),
            api.get("/vehicles"),
        ]);
        setFreights(f.data);
        setDrivers(d.data);
        setVehicles(v.data);
        setLoading(false);
    }, [mes]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm({ ...emptyForm, data: todayISO() }); setEditingId(null); setOpen(true); };
    const openEdit = (f) => {
        setForm({
            cliente_nome: f.cliente_nome, cliente_documento: f.cliente_documento || "",
            telefone: f.telefone || "", origem: f.origem, destino: f.destino,
            tipo_carga: f.tipo_carga, peso_volume: f.peso_volume || "",
            responsavel_nome: f.responsavel_nome, cpf: f.cpf || "",
            veiculo_modelo: f.veiculo_modelo || "", veiculo_placa: f.veiculo_placa,
            data: f.data, valor: f.valor, observacoes: f.observacoes || "",
        });
        setEditingId(f.id);
        setOpen(true);
    };

    const selectDriver = (id) => {
        const d = drivers.find((x) => x.id === id);
        if (d) setForm({ ...form, responsavel_nome: d.nome, cpf: d.cpf });
    };

    const selectVehicle = (id) => {
        const v = vehicles.find((x) => x.id === id);
        if (v) setForm({ ...form, veiculo_placa: v.placa, veiculo_modelo: v.modelo });
    };

    const save = async () => {
        if (!form.cliente_nome.trim() || !form.origem.trim() || !form.destino.trim() ||
            !form.tipo_carga.trim() || !form.responsavel_nome.trim() || !form.veiculo_placa.trim() || !form.data) {
            toast.error("Preencha os campos obrigatórios");
            return;
        }
        const payload = { ...form, valor: parseFloat(form.valor) || 0 };
        try {
            if (editingId) {
                await api.put(`/freights/${editingId}`, payload);
                toast.success("Frete atualizado!");
            } else {
                await api.post("/freights", payload);
                toast.success("Frete registrado!");
            }
            setOpen(false);
            load();
        } catch {
            toast.error("Erro ao salvar");
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/freights/${id}`);
            toast.success("Frete removido");
            load();
        } catch {
            toast.error("Erro ao remover");
        } finally {
            setConfirmDelete(null);
        }
    };

    const exportCsv = async () => {
        try {
            const params = {};
            if (mes) params.mes = mes;
            const res = await api.get("/freights/export", { params, responseType: "blob" });
            const filename = `fretes_${mes || "completo"}.csv`;
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch {
            toast.error("Erro ao exportar");
        }
    };

    const filtered = freights.filter((f) =>
        (f.cliente_nome + f.origem + f.destino + f.tipo_carga + f.responsavel_nome + f.veiculo_placa)
            .toLowerCase().includes(filter.toLowerCase())
    );
    const totalFiltered = filtered.reduce((a, f) => a + (f.valor || 0), 0);

    return (
        <div className="space-y-6" data-testid="freights-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-3xl font-extrabold text-foreground flex items-center gap-3">
                        <Truck className="w-7 h-7 text-primary" /> Fretes
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">Registro de fretes e transporte de cargas</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={exportCsv} data-testid="btn-export-freights" className="gap-2">
                        <Download className="w-4 h-4" /> Exportar CSV
                    </Button>
                    <Button onClick={openCreate} data-testid="btn-new-freight" className="gap-2">
                        <Plus className="w-4 h-4" /> Novo frete
                    </Button>
                </div>
            </div>

            <Card>
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 min-w-[220px] max-w-md">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por cliente, origem, destino, carga..."
                                className="pl-10"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                data-testid="input-search-freight"
                            />
                        </div>
                        <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="w-48" data-testid="input-month-freight" />
                        {mes && <Button variant="ghost" size="sm" onClick={() => setMes("")}>Limpar mês</Button>}
                        <div className="ml-auto text-sm">
                            <span className="text-muted-foreground">Total: </span>
                            <strong className="text-primary text-lg">{formatCurrency(totalFiltered)}</strong>
                            <span className="text-muted-foreground ml-2">({filtered.length} fretes)</span>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-muted-foreground py-8 text-center">Carregando...</div>
                    ) : filtered.length === 0 ? (
                        <div className="py-14 text-center border-2 border-dashed border-border rounded-xl">
                            <Truck className="w-10 h-10 mx-auto text-muted-foreground" />
                            <div className="mt-3 font-semibold">Nenhum frete cadastrado</div>
                            <div className="text-sm text-muted-foreground mt-1">Clique em "Novo frete" para registrar</div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-muted-foreground border-b border-border">
                                        <th className="py-3 px-2 font-semibold">Data</th>
                                        <th className="py-3 px-2 font-semibold">Cliente</th>
                                        <th className="py-3 px-2 font-semibold">Origem → Destino</th>
                                        <th className="py-3 px-2 font-semibold">Carga</th>
                                        <th className="py-3 px-2 font-semibold">Motorista</th>
                                        <th className="py-3 px-2 font-semibold">Placa</th>
                                        <th className="py-3 px-2 font-semibold text-right">Valor</th>
                                        <th className="py-3 px-2 font-semibold text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="freights-table-body">
                                    {filtered.map((f) => (
                                        <tr key={f.id} className="border-b border-border row-hover">
                                            <td className="py-3 px-2">{formatDateBR(f.data)}</td>
                                            <td className="py-3 px-2 font-medium">{f.cliente_nome}</td>
                                            <td className="py-3 px-2"><span className="text-muted-foreground">{f.origem}</span> <span className="mx-1">→</span> <strong>{f.destino}</strong></td>
                                            <td className="py-3 px-2">
                                                {f.tipo_carga}
                                                {f.peso_volume && <div className="text-xs text-muted-foreground">{f.peso_volume}</div>}
                                            </td>
                                            <td className="py-3 px-2">{f.responsavel_nome}</td>
                                            <td className="py-3 px-2 font-mono text-xs">{f.veiculo_placa}</td>
                                            <td className="py-3 px-2 text-right font-semibold">{formatCurrency(f.valor)}</td>
                                            <td className="py-3 px-2 text-right">
                                                <div className="flex gap-1 justify-end">
                                                    <Button variant="ghost" size="sm" onClick={() => window.open(`/frete/${f.id}/imprimir`, "_blank")} title="Imprimir" data-testid={`btn-print-freight-${f.id}`}>
                                                        <Printer className="w-4 h-4 text-primary" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openEdit(f)} data-testid={`btn-edit-freight-${f.id}`}>
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(f)} data-testid={`btn-delete-freight-${f.id}`}>
                                                        <Trash2 className="w-4 h-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl" data-testid="freight-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Editar frete" : "Novo frete"}</DialogTitle>
                        <DialogDescription>Registro de frete / transporte de carga</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-2">
                        <SectionTitle>Cliente / Contratante</SectionTitle>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Nome do cliente *</Label>
                                <Input value={form.cliente_nome} onChange={(e) => setForm({ ...form, cliente_nome: e.target.value })} data-testid="input-freight-cliente" />
                            </div>
                            <div>
                                <Label>CPF ou CNPJ</Label>
                                <Input value={form.cliente_documento} onChange={(e) => setForm({ ...form, cliente_documento: e.target.value })} data-testid="input-freight-doc" />
                            </div>
                        </div>
                        <div>
                            <Label>Telefone</Label>
                            <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="input-freight-tel" />
                        </div>

                        <SectionTitle>Carga e rota</SectionTitle>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Origem *</Label>
                                <Input value={form.origem} onChange={(e) => setForm({ ...form, origem: e.target.value })} placeholder="Ex: Belterra - PA" data-testid="input-freight-origem" />
                            </div>
                            <div>
                                <Label>Destino *</Label>
                                <Input value={form.destino} onChange={(e) => setForm({ ...form, destino: e.target.value })} placeholder="Ex: Santarém - PA" data-testid="input-freight-destino" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Tipo de carga *</Label>
                                <Input value={form.tipo_carga} onChange={(e) => setForm({ ...form, tipo_carga: e.target.value })} placeholder="Ex: Madeira, Produtos..." data-testid="input-freight-carga" />
                            </div>
                            <div>
                                <Label>Peso / volume</Label>
                                <Input value={form.peso_volume} onChange={(e) => setForm({ ...form, peso_volume: e.target.value })} placeholder="Ex: 2 toneladas, 500kg, 10m³" data-testid="input-freight-peso" />
                            </div>
                        </div>

                        <SectionTitle>Motorista e veículo</SectionTitle>
                        <div>
                            <Label>Selecionar motorista (opcional)</Label>
                            <Select onValueChange={selectDriver}>
                                <SelectTrigger data-testid="select-freight-driver"><SelectValue placeholder="Preencher automaticamente..." /></SelectTrigger>
                                <SelectContent>
                                    {drivers.length === 0 && <div className="p-3 text-xs text-muted-foreground">Nenhum motorista cadastrado</div>}
                                    {drivers.map((d) => (
                                        <SelectItem key={d.id} value={d.id}>{d.nome} — {formatCPF(d.cpf)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Motorista *</Label>
                                <Input value={form.responsavel_nome} onChange={(e) => setForm({ ...form, responsavel_nome: e.target.value })} data-testid="input-freight-resp" />
                            </div>
                            <div>
                                <Label>CPF</Label>
                                <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} data-testid="input-freight-cpf" />
                            </div>
                        </div>
                        <div>
                            <Label>Selecionar veículo (opcional)</Label>
                            <Select onValueChange={selectVehicle}>
                                <SelectTrigger data-testid="select-freight-vehicle"><SelectValue placeholder="Preencher automaticamente..." /></SelectTrigger>
                                <SelectContent>
                                    {vehicles.length === 0 && <div className="p-3 text-xs text-muted-foreground">Nenhum veículo cadastrado</div>}
                                    {vehicles.map((v) => (
                                        <SelectItem key={v.id} value={v.id}>{v.modelo} — {v.placa}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Modelo do veículo</Label>
                                <Input value={form.veiculo_modelo} onChange={(e) => setForm({ ...form, veiculo_modelo: e.target.value })} data-testid="input-freight-modelo" />
                            </div>
                            <div>
                                <Label>Placa *</Label>
                                <Input value={form.veiculo_placa} onChange={(e) => setForm({ ...form, veiculo_placa: e.target.value.toUpperCase() })} data-testid="input-freight-placa" />
                            </div>
                        </div>

                        <SectionTitle>Data e valor</SectionTitle>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Data *</Label>
                                <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="input-freight-data" />
                            </div>
                            <div>
                                <Label>Valor (R$) *</Label>
                                <Input type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} data-testid="input-freight-valor" />
                            </div>
                        </div>
                        <div>
                            <Label>Observações</Label>
                            <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="input-freight-obs" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        {editingId && (
                            <Button variant="secondary" className="gap-2" onClick={() => window.open(`/frete/${editingId}/imprimir`, "_blank")} data-testid="btn-print-dialog-freight">
                                <Printer className="w-4 h-4" /> Imprimir
                            </Button>
                        )}
                        <Button onClick={save} data-testid="btn-save-freight">Salvar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover frete?</AlertDialogTitle>
                        <AlertDialogDescription>
                            O frete de <strong>{confirmDelete?.cliente_nome}</strong> ({confirmDelete?.origem} → {confirmDelete?.destino}) será removido.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(confirmDelete.id)} data-testid="btn-confirm-delete-freight">Remover</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function SectionTitle({ children }) {
    return <div className="text-xs font-semibold uppercase tracking-wider text-primary border-b border-border pb-1 mt-2">{children}</div>;
}
